import { Badge } from '@vrplatform/components';
import {
  Icon,
  type IconDefinition,
  LightningIcon,
  LoaderIcon,
  ShuffleIcon,
  TimeIcon,
} from '@vrplatform/icons';
import { day, toTitleCase } from '@vrplatform/utils';
import { Anchor, Box } from '@mantine/core';
import { DrawerInfoCard } from '../../_components';
import { ConnectionEntityLinks } from './ConnectionEntityLinks';
import { type ResponseData, useTeam } from '@vrplatform/data';
import type { ComponentProps } from 'react';
import type { activeStatus_enum } from '@vrplatform/graphql';
import { Link } from 'react-router-dom';
import { pluralize } from '@vrplatform/ui-common';

type Connection = ResponseData<'get:/connections/{id}'> & {
  tenantId: string;
} & {
  workflows: {
    id: string;
    status: activeStatus_enum;
  }[];
};

type Props = {
  connection: Connection;
  cardProps?: Omit<ComponentProps<typeof DrawerInfoCard>, 'rows'>;
};

export const ConnectionInfoCard = ({ connection, cardProps }: Props) => {
  'use memo';

  const { id: connectionId, currentSync, tenantId } = connection;

  const [{ isGL }] = useTeam();

  const activeFlows =
    connection.workflows?.filter((workflow) => workflow.status === 'active')
      ?.length ?? 0;

  return (
    <DrawerInfoCard
      labelWidth={150}
      rows={[
        {
          icon: LoaderIcon,
          title: 'Status',
          text: (
            <StatusBadge
              currentSync={currentSync}
              status={connection.status}
              isErrorState={connection.isErrorState}
            />
          ),
        },
        {
          icon: TimeIcon,
          title: 'Last sync',
          text: currentSync?.createdAt
            ? day(currentSync.createdAt).format('MMM DD, YYYY HH:mm')
            : null,
        },
        {
          icon: ShuffleIcon,
          title: 'Synced',
          text: (
            <ConnectionEntityLinks
              connectionId={connectionId}
              tenantId={tenantId}
            />
          ),
        },
        {
          icon: LightningIcon,
          title: 'Active Workflows',
          text:
            !isGL || !connection.workflows?.length ? null : (
              <Anchor
                c="orange"
                component={Link}
                to={`/connection/${connection.id}/workflows`}
              >
                {activeFlows} {pluralize(activeFlows, 'Workflow')}
              </Anchor>
            ),
        },
      ]}
      {...cardProps}
    />
  );
};

const StatusBadge = ({
  status,
  currentSync,
  isErrorState,
}: {
  currentSync: Connection['currentSync'];
  status: Connection['status'];
  isErrorState: Connection['isErrorState'];
}) => {
  'use memo';

  let color: 'green' | 'yellow' | 'red' | 'orange' = 'green';
  let label = 'Active';
  let icon: IconDefinition | undefined;

  const syncStatus = currentSync?.status;

  if (isErrorState) {
    color = 'red';
    label = 'Error';
    icon = 'CrossIcon';
  } else if (syncStatus === 'failed') {
    color = 'orange';
    label = 'Last Sync Failed';
    icon = 'AlertTriangleIcon';
  } else if (syncStatus === 'queued' || syncStatus === 'started') {
    label = toTitleCase(syncStatus);
    color = 'yellow';
  }

  if (status === 'inactive') {
    label = 'Archived';
    color = 'yellow';
  }

  return (
    <Box>
      <Badge
        leftIcon={icon && <Icon icon={icon} color={color} size={12} />}
        color={color}
      >
        {label}
      </Badge>
    </Box>
  );
};
